import React, { useState, useCallback, useEffect, useMemo } from 'react';
import BasicInfoSection from './sections/BasicInfoSection';
import ThematicAxesSection from './sections/ThematicAxesSection';
import ParticipationModalitySection from './sections/ParticipationModalitySection';
import ExpoZoneSection from './sections/ExpoZoneSection';
import MultiSelectTags from './MultiSelectTags';
import SearchableSelect from './SearchableSelect';
import { BasicInstitutionalInfo, InstitutionalDescription, ParticipationModality, EventProgramParticipation } from '../types/profile.types';
import { participationModalities as defaultParticipationModalities } from '../data/profileData';
import './CreateContactModal.css';

const emptyBasicInfo: BasicInstitutionalInfo = {
  organizationName: '',
  organizationType: '',
  country: '',
  website: '',
  industries: [],
  eventProgramParticipation: [],
  socialNetworks: {},
  thematicAxes: [],
};

const emptyInstitutionalDescription: InstitutionalDescription = {
  description: '',
};

export interface CreateOrganizationContactOption {
  id: number;
  nombre?: string | null;
  apellido?: string | null;
  correo?: string | null;
  organization_name?: string | null;
}

export interface CreateOrganizationPipelineOption {
  id: number;
  name: string;
  field_schema?: { key: string; label: string }[];
}

export interface CreateOrganizationStageOption {
  id: string;
  label: string;
  color: string;
  position: number;
}

export interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactsList?: CreateOrganizationContactOption[];
  pipelinesList?: CreateOrganizationPipelineOption[];
  /** Etapas por pipeline para desplegable Etapa al crear negocio */
  stagesByPipelineId?: Record<number, CreateOrganizationStageOption[]>;
  isDarkMode?: boolean;
  asSidebar?: boolean;
  /** Opciones de modalidad para pipeline Scesde26 */
  participationModalityOptions?: { value: string; label: string }[];
}

const DEFAULT_STAGES: CreateOrganizationStageOption[] = [
  { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0 },
  { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1 },
  { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2 },
  { id: 'en_proceso', label: 'En proceso', color: '#f59e0b', position: 3 },
  { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4 },
  { id: 'completado', label: 'Completado', color: '#22c55e', position: 5 },
  { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6 },
];

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  contactsList = [],
  pipelinesList = [],
  stagesByPipelineId = {},
  isDarkMode = false,
  asSidebar = false,
  participationModalityOptions = defaultParticipationModalities,
}) => {
  const [basicInfo, setBasicInfo] = useState<BasicInstitutionalInfo>(emptyBasicInfo);
  const [institutionalDescription, setInstitutionalDescription] = useState<InstitutionalDescription>(emptyInstitutionalDescription);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedPipelineIdForNegocio, setSelectedPipelineIdForNegocio] = useState<number | null>(null);
  const [showCreateNegocioForm, setShowCreateNegocioForm] = useState(false);
  const [newNegocioStageId, setNewNegocioStageId] = useState('');
  const [newNegocioPriority, setNewNegocioPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [otherPipelineDealData, setOtherPipelineDealData] = useState<Record<string, string>>({});
  const [participationModalities, setParticipationModalities] = useState<ParticipationModality[]>([]);
  const [agreedModality, setAgreedModality] = useState('');
  const [modalityObservations, setModalityObservations] = useState('');
  const [eventProgramParticipation, setEventProgramParticipation] = useState<string[]>([]);
  const [requestInformation, setRequestInformation] = useState(false);
  const [requestExpoInformation, setRequestExpoInformation] = useState(false);
  const [selectedStands, setSelectedStands] = useState<string[]>([]);
  const [confirmedStands, setConfirmedStands] = useState<string[]>([]);

  const resetForm = useCallback(() => {
    setBasicInfo(emptyBasicInfo);
    setInstitutionalDescription(emptyInstitutionalDescription);
    setSelectedContactIds([]);
    setSelectedPipelineIdForNegocio(null);
    setShowCreateNegocioForm(false);
    setNewNegocioStageId('');
    setNewNegocioPriority('medium');
    setOtherPipelineDealData({});
    setParticipationModalities([]);
    setAgreedModality('');
    setModalityObservations('');
    setEventProgramParticipation([]);
    setRequestInformation(false);
    setRequestExpoInformation(false);
    setSelectedStands([]);
    setConfirmedStands([]);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (showCreateNegocioForm && selectedPipelineIdForNegocio != null && newNegocioStageId === '') {
      const stages = (stagesByPipelineId[selectedPipelineIdForNegocio] || DEFAULT_STAGES).slice().sort((a, b) => a.position - b.position);
      if (stages.length > 0) setNewNegocioStageId(stages[0].id);
    }
  }, [showCreateNegocioForm, selectedPipelineIdForNegocio, stagesByPipelineId, newNegocioStageId]);

  const contactOptions = useMemo(() => {
    const used = new Set<string>();
    return contactsList.map((c) => {
      const hasName = [c.nombre, c.apellido].filter(Boolean).join(' ').trim() !== '';
      const base = hasName
        ? (c.correo ? `${[c.nombre, c.apellido].filter(Boolean).join(' ')} (${c.correo})` : [c.nombre, c.apellido].filter(Boolean).join(' '))
        : (c.correo ? c.correo : `Sin nombre · #${c.id}`);
      const label = used.has(base) ? `${base} · #${c.id}` : base;
      used.add(base);
      return { id: c.id, label };
    });
  }, [contactsList]);

  const contactOptionLabels = useMemo(() => contactOptions.map((o) => o.label), [contactOptions]);

  const selectedContactLabels = useMemo(
    () => selectedContactIds.map((id) => contactOptions.find((o) => o.id === id)?.label).filter((l): l is string => !!l),
    [selectedContactIds, contactOptions]
  );

  const handleContactSelectChange = useCallback(
    (labels: string[]) => {
      setSelectedContactIds(labels.map((l) => contactOptions.find((o) => o.label === l)?.id).filter((id): id is number => id != null));
    },
    [contactOptions]
  );

  const pipelineSelectOptions = useMemo(() => pipelinesList.map((p) => p.name), [pipelinesList]);
  const selectedPipelineForNegocio = selectedPipelineIdForNegocio != null ? pipelinesList.find((p) => p.id === selectedPipelineIdForNegocio) : null;
  const pipelineSelectValue = selectedPipelineForNegocio ? selectedPipelineForNegocio.name : '';

  const handleSubmit = () => {
    // Solo front: no llamamos al backend
    handleClose();
  };

  if (!isOpen) return null;

  const formContent = (
    <>
      <div className="create-contact-modal-header">
        <h2>Crear organización</h2>
        <button type="button" className="create-contact-modal-close" onClick={handleClose} aria-label="Cerrar">
          ×
        </button>
      </div>
      <div className="create-contact-modal-body">
        <section className="create-contact-section">
          <h3>Datos de la organización</h3>
          <BasicInfoSection
            data={basicInfo}
            onChange={setBasicInfo}
            institutionalDescription={institutionalDescription}
            onInstitutionalDescriptionChange={setInstitutionalDescription}
            createContactMode
          />
          <ThematicAxesSection
            selectedAxes={basicInfo.thematicAxes || []}
            onChange={(axes) => setBasicInfo((prev) => ({ ...prev, thematicAxes: axes }))}
            createContactMode
          />
        </section>

        <section className="create-contact-section">
          <h3>Asociar contactos</h3>
          <div className="create-contact-associate-org">
            <label className="create-contact-label">Contactos vinculados a esta organización</label>
            {contactsList.length === 0 ? (
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 12 }}>No hay contactos cargados.</p>
            ) : (
              <MultiSelectTags
                options={contactOptionLabels}
                selectedValues={selectedContactLabels}
                onChange={handleContactSelectChange}
                placeholder="Buscar o seleccionar contactos..."
              />
            )}
          </div>
        </section>

        <section className="create-contact-section">
          <h3>Asociar negocios</h3>
          <div className="create-contact-associate-deal">
            <p className="create-contact-hint">Seleccione un pipeline y use el botón para crear un negocio en ese pipeline con los campos personalizados.</p>
            {pipelinesList.length === 0 ? (
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 12 }}>No hay pipelines cargados.</p>
            ) : (
              <>
                <div className="create-contact-deal-row">
                  <label className="create-contact-label">Pipeline</label>
                  <SearchableSelect
                    value={pipelineSelectValue}
                    onChange={(value) => {
                      const p = pipelinesList.find((x) => x.name === value);
                      setSelectedPipelineIdForNegocio(p ? p.id : null);
                      setShowCreateNegocioForm(false);
                      setOtherPipelineDealData({});
                    }}
                    options={pipelineSelectOptions}
                    placeholder="Seleccionar pipeline..."
                    allowCustomInput={false}
                    allowSearch={false}
                  />
                </div>
                {selectedPipelineIdForNegocio != null && (
                  <button
                    type="button"
                    className="create-contact-link-btn"
                    style={{ marginTop: 8 }}
                    onClick={() => setShowCreateNegocioForm(true)}
                  >
                    Crear negocio en este pipeline
                  </button>
                )}
              </>
            )}
          </div>
        </section>

        {showCreateNegocioForm && selectedPipelineIdForNegocio != null && (() => {
          const isScesde26 = selectedPipelineIdForNegocio === 1 || pipelinesList.find((p) => p.id === selectedPipelineIdForNegocio)?.name === 'Scesde26';
          const pipelineNameForLabel = pipelineSelectValue || 'seleccionado';
          const stagesForPipeline = (stagesByPipelineId[selectedPipelineIdForNegocio] || DEFAULT_STAGES).slice().sort((a, b) => a.position - b.position);
          const stageOptionsOrg = stagesForPipeline.map((s) => s.label);
          const selectedStageLabelOrg = newNegocioStageId ? stagesForPipeline.find((s) => s.id === newNegocioStageId)?.label ?? '' : (stagesForPipeline[0]?.label ?? '');
          const priorityOptionsOrg = ['Alta', 'Media', 'Baja'];
          const priorityLabelOrg = newNegocioPriority === 'high' ? 'Alta' : newNegocioPriority === 'low' ? 'Baja' : 'Media';
          const PRIORITY_COLORS: Record<string, string> = { Alta: '#ef4444', Media: '#f59e0b', Baja: '#22c55e' };
          const getPriorityColorOrg = (l: string) => PRIORITY_COLORS[l] ?? '#94a3b8';
          const selectedStageColorOrg = newNegocioStageId ? stagesForPipeline.find((s) => s.id === newNegocioStageId)?.color : (stagesForPipeline[0]?.color ?? '#94a3b8');
          const getStageOptionColorOrg = (label: string) => stagesForPipeline.find((s) => s.label === label)?.color;

          const etapaPrioridadSectionOrg = (
            <section className="create-contact-section">
              <h3>Etapa y prioridad</h3>
              <div className="create-contact-associate-deal">
                {stagesForPipeline.length > 0 && (
                  <div className="create-contact-deal-row">
                    <label className="create-contact-label">Etapa</label>
                    <div style={{ marginBottom: 12 }}>
                      <SearchableSelect
                        value={selectedStageLabelOrg}
                        onChange={(value) => {
                          const s = stagesForPipeline.find((x) => x.label === value);
                          setNewNegocioStageId(s ? s.id : '');
                        }}
                        options={stageOptionsOrg}
                        placeholder="Seleccionar etapa..."
                        allowCustomInput={false}
                        allowSearch={false}
                        prefix={<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: selectedStageColorOrg, flexShrink: 0 }} aria-hidden />}
                        getOptionColor={getStageOptionColorOrg}
                      />
                    </div>
                  </div>
                )}
                <div className="create-contact-deal-row">
                  <label className="create-contact-label">Prioridad</label>
                  <div style={{ marginBottom: 12 }}>
                    <SearchableSelect
                      value={priorityLabelOrg}
                      onChange={(value) => setNewNegocioPriority(value === 'Alta' ? 'high' : value === 'Baja' ? 'low' : 'medium')}
                      options={priorityOptionsOrg}
                      placeholder="Seleccionar prioridad..."
                      allowCustomInput={false}
                      allowSearch={false}
                      prefix={<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: getPriorityColorOrg(priorityLabelOrg), flexShrink: 0 }} aria-hidden />}
                      getOptionColor={getPriorityColorOrg}
                    />
                  </div>
                </div>
              </div>
            </section>
          );

          return (
            <>
              <section className="create-contact-section">
                <p className="create-contact-hint" style={{ marginBottom: 8 }}>
                  Pipeline seleccionado: <strong>{pipelineNameForLabel}</strong>. Complete los datos para crear el negocio.
                </p>
                <p className="create-contact-label" style={{ marginBottom: 12 }}>
                  La organización no tiene negocio en el pipeline {pipelineNameForLabel}. Complete los datos a continuación.
                </p>
              </section>
              {etapaPrioridadSectionOrg}
              {isScesde26 ? (
              <>
                <section className="create-contact-section">
                  <h3>Modalidad de participación</h3>
                  <ParticipationModalitySection
                    selectedModalities={participationModalities}
                    onChange={setParticipationModalities}
                    agreedModality={agreedModality}
                    modalityObservations={modalityObservations}
                    eventProgramParticipation={eventProgramParticipation as EventProgramParticipation[]}
                    onEventProgramParticipationChange={setEventProgramParticipation}
                    requestInformation={requestInformation}
                    onRequestInformationChange={setRequestInformation}
                    createContactMode
                  />
                </section>
                <section className="create-contact-section create-contact-stands-wrap">
                  <h3>Stands de interés</h3>
                  <ExpoZoneSection
                    selectedStands={selectedStands}
                    confirmedStands={confirmedStands}
                    onChange={setSelectedStands}
                    onConfirmedChange={setConfirmedStands}
                    requestInformation={requestExpoInformation}
                    onRequestInformationChange={setRequestExpoInformation}
                    createContactMode
                  />
                </section>
              </>
              ) : (() => {
                const selectedPipeline = pipelinesList.find((p) => p.id === selectedPipelineIdForNegocio);
                const otherPipelineName = selectedPipeline?.name ?? 'este pipeline';
                const otherFieldSchema = selectedPipeline?.field_schema ?? [];
                if (otherFieldSchema.length === 0) {
                  return (
                    <section className="create-contact-section">
                      <p className="create-contact-hint">
                        El negocio se creará en el pipeline <strong>{otherPipelineName}</strong>. Este pipeline no tiene campos personalizados definidos.
                      </p>
                    </section>
                  );
                }
                return (
                  <section className="create-contact-section">
                    <p className="create-contact-hint">Complete los campos del pipeline seleccionado.</p>
                    <div className="create-contact-associate-deal">
                      {otherFieldSchema.map((field) => (
                        <div key={field.key} className="create-contact-deal-row">
                          <label className="create-contact-label" htmlFor={`create-org-deal-${field.key}`}>
                            {(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}
                          </label>
                          <input
                            id={`create-org-deal-${field.key}`}
                            type="text"
                            className="create-contact-input"
                            value={otherPipelineDealData[field.key] ?? ''}
                            onChange={(e) => setOtherPipelineDealData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            placeholder={(field.label || '').replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })()}
            </>
          );
        })()}
      </div>
      <div className="create-contact-modal-footer">
        <button type="button" className="create-contact-footer-btn secondary" onClick={handleClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="create-contact-footer-btn primary"
          onClick={handleSubmit}
        >
          Crear organización
        </button>
      </div>
    </>
  );

  if (asSidebar) {
    return (
      <div className={`create-contact-sidebar-inner ${isDarkMode ? 'dark' : ''}`}>
        {formContent}
      </div>
    );
  }

  return (
    <div className="create-contact-modal-overlay" onClick={handleClose}>
      <div
        className={`create-contact-modal ${isDarkMode ? 'dark' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {formContent}
      </div>
    </div>
  );
};

export default CreateOrganizationModal;
